import { 
  collection, 
  doc, 
  addDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc, 
  setDoc,
  query, 
  where, 
  orderBy, 
  arrayUnion
} from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Learning Components Service
 * Manages interactive learning components that can be added to sessions, courses, or topics
 */

const COLLECTIONS = {
  COMPONENTS: 'learningComponents',
  COURSE_COMPONENTS: 'courseComponents',
  SESSION_COMPONENTS: 'sessionComponents',
  TOPIC_COMPONENTS: 'topicComponents'
};

export class LearningComponentsService {
  /**
   * Get all available learning components
   */
  static async getAllComponents() {
    try {
      const componentsRef = collection(db, COLLECTIONS.COMPONENTS);
      const q = query(componentsRef, orderBy('category', 'asc'), orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting learning components:', error);
      
      // Check if it's a permission error
      if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
        throw new Error('Permission denied. Please ensure Firestore security rules are deployed. See DEPLOY_FIRESTORE_RULES.md for instructions.');
      }
      
      throw error;
    }
  }

  /**
   * Get components by category
   */
  static async getComponentsByCategory(category) {
    try {
      const componentsRef = collection(db, COLLECTIONS.COMPONENTS);
      const q = query(
        componentsRef, 
        where('category', '==', category),
        orderBy('name', 'asc')
      );
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error getting components by category:', error);
      throw error;
    }
  }

  /**
   * Create a new learning component
   */
  static async createComponent(componentData) {
    try {
      const component = {
        name: componentData.name,
        description: componentData.description,
        category: componentData.category,
        type: componentData.type, // 'interactive', 'assessment', 'collaborative', 'creative'
        config: componentData.config || {},
        settings: componentData.settings || {},
        preview: componentData.preview || '',
        difficulty: componentData.difficulty || 'beginner', // 'beginner', 'intermediate', 'advanced'
        estimatedTime: componentData.estimatedTime || 5, // minutes
        tags: componentData.tags || [],
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: componentData.createdBy
      };

      const docRef = await addDoc(collection(db, COLLECTIONS.COMPONENTS), component);
      return { id: docRef.id, ...component };
    } catch (error) {
      console.error('Error creating learning component:', error);
      throw error;
    }
  }

  /**
   * Update a learning component
   */
  static async updateComponent(componentId, updateData) {
    try {
      const componentRef = doc(db, COLLECTIONS.COMPONENTS, componentId);
      await updateDoc(componentRef, {
        ...updateData,
        updatedAt: new Date()
      });
      return true;
    } catch (error) {
      console.error('Error updating learning component:', error);
      throw error;
    }
  }

  /**
   * Add a new learning component
   */
  static async addComponent(componentData) {
    try {
      const componentsRef = collection(db, COLLECTIONS.COMPONENTS);
      const docRef = await addDoc(componentsRef, {
        ...componentData,
        createdAt: new Date(),
        updatedAt: new Date()
      });
      
      // Return the created component with ID
      return {
        id: docRef.id,
        ...componentData,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Error adding learning component:', error);
      
      // Check if it's a permission error
      if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
        throw new Error('Permission denied. You must be an admin to add components, and Firestore security rules must be deployed. See DEPLOY_FIRESTORE_RULES.md for instructions.');
      }
      
      throw error;
    }
  }

  /**
   * Delete a learning component
   */
  static async deleteComponent(componentId) {
    try {
      const componentRef = doc(db, COLLECTIONS.COMPONENTS, componentId);
      await deleteDoc(componentRef);
      return true;
    } catch (error) {
      console.error('Error deleting learning component:', error);
      throw error;
    }
  }

  /**
   * Add components to a course
   */
  static async addComponentsToCourse(courseId, componentIds, addedBy) {
    try {
      const courseComponentsRef = doc(db, COLLECTIONS.COURSE_COMPONENTS, courseId);
      const courseDoc = await getDoc(courseComponentsRef);
      
      const components = componentIds.map(id => ({
        componentId: id,
        addedAt: new Date(),
        addedBy: addedBy,
        isActive: true
      }));

      if (courseDoc.exists()) {
        await updateDoc(courseComponentsRef, {
          components: arrayUnion(...components),
          updatedAt: new Date()
        });
      } else {
        await setDoc(courseComponentsRef, {
          courseId: courseId,
          components: components,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
      return true;
    } catch (error) {
      console.error('Error adding components to course:', error);
      throw error;
    }
  }

  /**
   * Add components to a session
   */
  static async addComponentsToSession(sessionId, componentIds, addedBy) {
    try {
      const sessionComponentsRef = doc(db, COLLECTIONS.SESSION_COMPONENTS, sessionId);
      const sessionDoc = await getDoc(sessionComponentsRef);
      
      const components = componentIds.map(id => ({
        componentId: id,
        addedAt: new Date(),
        addedBy: addedBy,
        isActive: true
      }));

      if (sessionDoc.exists()) {
        await updateDoc(sessionComponentsRef, {
          components: arrayUnion(...components),
          updatedAt: new Date()
        });
      } else {
        await setDoc(sessionComponentsRef, {
          sessionId: sessionId,
          components: components,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
      return true;
    } catch (error) {
      console.error('Error adding components to session:', error);
      throw error;
    }
  }

  /**
   * Add components to a topic
   */
  static async addComponentsToTopic(topicId, componentIds, addedBy) {
    try {
      const topicComponentsRef = doc(db, COLLECTIONS.TOPIC_COMPONENTS, topicId);
      const topicDoc = await getDoc(topicComponentsRef);
      
      const components = componentIds.map(id => ({
        componentId: id,
        addedAt: new Date(),
        addedBy: addedBy,
        isActive: true
      }));

      if (topicDoc.exists()) {
        await updateDoc(topicComponentsRef, {
          components: arrayUnion(...components),
          updatedAt: new Date()
        });
      } else {
        await setDoc(topicComponentsRef, {
          topicId: topicId,
          components: components,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      }
      return true;
    } catch (error) {
      console.error('Error adding components to topic:', error);
      throw error;
    }
  }

  /**
   * Get components for a course
   */
  static async getCourseComponents(courseId) {
    try {
      const courseComponentsRef = doc(db, COLLECTIONS.COURSE_COMPONENTS, courseId);
      const courseDoc = await getDoc(courseComponentsRef);
      
      if (!courseDoc.exists()) {
        return [];
      }

      const courseData = courseDoc.data();
      const componentIds = courseData.components
        .filter(c => c.isActive)
        .map(c => c.componentId);

      // Remove duplicate component IDs to prevent duplicate keys
      const uniqueComponentIds = [...new Set(componentIds)];

      // Get full component details
      const components = [];
      for (const id of uniqueComponentIds) {
        const componentRef = doc(db, COLLECTIONS.COMPONENTS, id);
        const componentDoc = await getDoc(componentRef);
        if (componentDoc.exists()) {
          components.push({
            id: componentDoc.id,
            ...componentDoc.data()
          });
        }
      }

      return components;
    } catch (error) {
      console.error('Error getting course components:', error);
      throw error;
    }
  }

  /**
   * Get components for a session
   */
  static async getSessionComponents(sessionId) {
    try {
      const sessionComponentsRef = doc(db, COLLECTIONS.SESSION_COMPONENTS, sessionId);
      const sessionDoc = await getDoc(sessionComponentsRef);
      
      if (!sessionDoc.exists()) {
        return [];
      }

      const sessionData = sessionDoc.data();
      const componentIds = sessionData.components
        .filter(c => c.isActive)
        .map(c => c.componentId);

      // Remove duplicate component IDs to prevent duplicate keys
      const uniqueComponentIds = [...new Set(componentIds)];

      // Get full component details
      const components = [];
      for (const id of uniqueComponentIds) {
        const componentRef = doc(db, COLLECTIONS.COMPONENTS, id);
        const componentDoc = await getDoc(componentRef);
        if (componentDoc.exists()) {
          components.push({
            id: componentDoc.id,
            ...componentDoc.data()
          });
        }
      }

      return components;
    } catch (error) {
      console.error('Error getting session components:', error);
      throw error;
    }
  }

  /**
   * Get components for a topic
   */
  static async getTopicComponents(topicId) {
    try {
      const topicComponentsRef = doc(db, COLLECTIONS.TOPIC_COMPONENTS, topicId);
      const topicDoc = await getDoc(topicComponentsRef);
      
      if (!topicDoc.exists()) {
        return [];
      }

      const topicData = topicDoc.data();
      const componentIds = topicData.components
        .filter(c => c.isActive)
        .map(c => c.componentId);

      // Get full component details
      const components = [];
      for (const id of componentIds) {
        const componentRef = doc(db, COLLECTIONS.COMPONENTS, id);
        const componentDoc = await getDoc(componentRef);
        if (componentDoc.exists()) {
          components.push({
            id: componentDoc.id,
            ...componentDoc.data()
          });
        }
      }

      return components;
    } catch (error) {
      console.error('Error getting topic components:', error);
      throw error;
    }
  }

  /**
   * Remove a component from a course
   */
  static async removeComponentFromCourse(courseId, componentId) {
    try {
      const courseComponentsRef = doc(db, COLLECTIONS.COURSE_COMPONENTS, courseId);
      const courseComponentsDoc = await getDoc(courseComponentsRef);
      
      if (courseComponentsDoc.exists()) {
        const currentData = courseComponentsDoc.data();
        const updatedComponents = currentData.components.filter(
          comp => comp.componentId !== componentId
        );
        
        await updateDoc(courseComponentsRef, {
          components: updatedComponents,
          updatedAt: new Date()
        });
      }
    } catch (error) {
      console.error('Error removing component from course:', error);
      throw error;
    }
  }

  /**
   * Update session component configuration
   */
  static async updateSessionComponentConfig(sessionId, componentId, config) {
    try {
      const sessionComponentsRef = doc(db, COLLECTIONS.SESSION_COMPONENTS, sessionId);
      const sessionComponentsDoc = await getDoc(sessionComponentsRef);
      
      if (sessionComponentsDoc.exists()) {
        const currentData = sessionComponentsDoc.data();
        const updatedComponents = currentData.components.map(comp =>
          comp.componentId === componentId
            ? { ...comp, config: { ...comp.config, ...config }, updatedAt: new Date() }
            : comp
        );
        
        await updateDoc(sessionComponentsRef, {
          components: updatedComponents,
          updatedAt: new Date()
        });
      }
    } catch (error) {
      console.error('Error updating session component config:', error);
      throw error;
    }
  }

  /**
   * Get effective components for a session (with inheritance)
   */
  static async getEffectiveSessionComponents(sessionId, courseId) {
    try {
      // Get session components (overrides)
      const sessionComponents = await this.getSessionComponents(sessionId);
      
      // Get course components (inherited)
      let courseComponents = [];
      if (courseId) {
        courseComponents = await this.getCourseComponents(courseId);
      }
      
      // Merge with session components taking precedence
      const componentMap = new Map();
      
      // Add inherited components first
      courseComponents.forEach(comp => {
        componentMap.set(comp.id, { ...comp, inherited: true, source: 'course' });
      });
      
      // Override with session components
      sessionComponents.forEach(comp => {
        componentMap.set(comp.id, { ...comp, inherited: false, source: 'session' });
      });
      
      return Array.from(componentMap.values());
    } catch (error) {
      console.error('Error getting effective session components:', error);
      throw error;
    }
  }

  /**
   * Get effective components for a topic (with inheritance)
   */
  static async getEffectiveTopicComponents(topicId, courseId) {
    try {
      // Get topic components (overrides)
      const topicComponents = await this.getTopicComponents(topicId);
      
      // Get course components (inherited)
      let courseComponents = [];
      if (courseId) {
        courseComponents = await this.getCourseComponents(courseId);
      }
      
      // Merge with topic components taking precedence
      const componentMap = new Map();
      
      // Add inherited components first
      courseComponents.forEach(comp => {
        componentMap.set(comp.id, { ...comp, inherited: true, source: 'course' });
      });
      
      // Override with topic components
      topicComponents.forEach(comp => {
        componentMap.set(comp.id, { ...comp, inherited: false, source: 'topic' });
      });
      
      return Array.from(componentMap.values());
    } catch (error) {
      console.error('Error getting effective topic components:', error);
      throw error;
    }
  }

  /**
   * Remove component from session
   */
  static async removeComponentFromSession(sessionId, componentId) {
    try {
      const sessionComponentsRef = doc(db, COLLECTIONS.SESSION_COMPONENTS, sessionId);
      const sessionComponentsDoc = await getDoc(sessionComponentsRef);
      
      if (sessionComponentsDoc.exists()) {
        const currentData = sessionComponentsDoc.data();
        const updatedComponents = currentData.components.filter(
          comp => comp.componentId !== componentId
        );
        
        await updateDoc(sessionComponentsRef, {
          components: updatedComponents,
          updatedAt: new Date()
        });
      }
      return true;
    } catch (error) {
      console.error('Error removing component from session:', error);
      throw error;
    }
  }

  /**
   * Remove component from topic
   */
  static async removeComponentFromTopic(topicId, componentId) {
    try {
      const topicComponentsRef = doc(db, COLLECTIONS.TOPIC_COMPONENTS, topicId);
      const topicDoc = await getDoc(topicComponentsRef);
      
      if (topicDoc.exists()) {
        const components = topicDoc.data().components.map(c => 
          c.componentId === componentId ? { ...c, isActive: false } : c
        );
        await updateDoc(topicComponentsRef, {
          components: components,
          updatedAt: new Date()
        });
      }
      return true;
    } catch (error) {
      console.error('Error removing component from topic:', error);
      throw error;
    }
  }
}

export default LearningComponentsService;
